// src/components/auth/components/LoginMonitor.tsx
import React from "react";
import styled from "styled-components";
import { Typography, Table } from "antd";
import { RightOutlined } from "@ant-design/icons";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import MiniEstOLSLM from "./MiniEstOLSLM";
import MiniOlsmeMonitor from "./MiniOlsmeMonitor";
import "./LoginMonitor.css";

// Type Definitions
interface Asset {
  label: string;
  value: string;
  link: string;
  percentage?: string;
}

interface Profile {
  key: string;
  link: string;
  image: string;
  name: string;
  olsDays: string;
  stats: string;
  olsm: string;
}

interface SalesData {
  students: string;
  assets: Asset[];
  estateValues: string[];
}

interface LoginMonitorProps {
  toggleEstate: () => void;
  state: { isEstateOpen?: boolean };
}

const { Text } = Typography;

// Styled Components
const MonitorContainer = styled.div`
  padding: 40px 30px;
  background: #000000;
  color: #ffffff;
  border-radius: 12px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.5);
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  display: flex;
  flex-direction: column;
  align-items: flex-start;

  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 6px 25px rgba(0, 0, 0, 0.7);
  }
`;

const AssetsWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  width: 100%;
  gap: 20px;
  margin-top: 20px;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 10px;
  }
`;

const AssetSection = styled.div`
  flex: 1;
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 8px;
  padding: 10px;
  background: rgba(255, 255, 255, 0.05);
`;

const LightSavingsSection = styled.div`
  flex: 1;
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 8px;
  padding: 10px;
  background: rgba(255, 255, 255, 0.05);
`;

const OlsmeSection = styled.div`
  width: 100%;
  margin-top: 20px;
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 8px;
  padding: 10px;
  background: rgba(255, 255, 255, 0.05);
`;

const StudentCountSection = styled.div`
  display: flex;
  align-items: center;
  margin: 20px 0;
  padding: 10px;
  border-radius: 8px;
  transition: background 0.3s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.1);
  }
`;

const LogoImage = styled.img`
  width: 60px;
  height: 30px;
  margin-right: 20px;
  border-radius: 4px;
  object-fit: cover;
`;

interface ActionButtonProps {
  $color?: string;
  $bgColor?: string;
  $large?: boolean;
}

const ActionButton = styled.a<ActionButtonProps>`
  color: ${(props) => props.$color || "#ffffff"};
  background-color: ${(props) => props.$bgColor || "#3552fc"};
  padding: ${(props) => (props.$large ? "10px 20px" : "8px 16px")};
  margin-left: 10px;
  border-radius: 6px;
  text-decoration: none;
  font-size: ${(props) => (props.$large ? "18px" : "16px")};
  font-weight: 500;
  transition: background 0.3s ease, transform 0.3s ease;

  &:hover {
    background-color: ${(props) =>
      props.$bgColor ? darkenColor(props.$bgColor, 10) : "#2a43d6"};
    transform: scale(1.05);
  }

  &:focus {
    outline: 2px solid #ffd700;
    outline-offset: 2px;
  }
`;

const darkenColor = (color: string, percent: number): string => {
  const num = parseInt(color.replace("#", ""), 16),
    amt = Math.round(2.55 * percent),
    R = (num >> 16) - amt,
    G = (num >> 8 & 0x00ff) - amt,
    B = (num & 0x0000ff) - amt;
  return `#${(
    0x1000000 +
    (R < 255 ? (R < 1 ? 0 : R) : 255) * 0x10000 +
    (G < 255 ? (G < 1 ? 0 : G) : 255) * 0x100 +
    (B < 255 ? (B < 1 ? 0 : B) : 255)
  )
    .toString(16)
    .slice(1)}`;
};

const SalesHighlight = styled(Text)`
  color: #ffd700;
  font-weight: 600;
  font-size: 16px;
`;

const StudentCountLink = styled(Link)`
  color: #ffffff;
  text-decoration: none;
  transition: color 0.3s ease;

  &:hover {
    color: #f0f0f0;
  }

  &:focus {
    outline: 2px solid #ffd700;
    outline-offset: 2px;
  }
`;

const EstateValueLink = styled(Link)`
  color: #ff4d4f;
  text-decoration: none;
  transition: color 0.3s ease;

  &:hover {
    color: #ff7875;
  }

  &:focus {
    outline: 2px solid #ffd700;
    outline-offset: 2px;
  }
`;

const ProfileContainer = styled.div`
  background: #111;
  color: #ffffff;
  width: 100%;
  padding: 20px;
  border-radius: 12px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.5);
  transition: transform 0.3s ease, box-shadow 0.3s ease;

  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 6px 25px rgba(0, 0, 0, 0.7);
  }
`;

const LoginMonitor: React.FC<LoginMonitorProps> = ({ toggleEstate, state }) => {
  const salesData: SalesData = {
    students: "27450.OLSLM eae inuo students",
    assets: [
      { label: "379", value: "379", link: "/aOLSLMasset" },
      { label: "1.45", value: "1.45", link: "/aOLSLMasset" },
      { label: "346 - OLS9LM", value: "346", link: "/aOLSLMasset", percentage: "1.8 %" },
      { label: "33 - OLS4LM", value: "33", link: "/aOLSLMasset", percentage: "0.5 %" },
    ],
    estateValues: ["104000 $", "49000 $", "39000 $", "29000 $", "19000 $", "18000 $"],
  };

  const estateValueToIdMap: { [key: string]: string } = {
    "104000 $": "mount-shasta-2.5-acres",
    "49000 $": "red-sea-85-sqm",
    "39000 $": "red-sea-75-sqm",
    "29000 $": "red-sea-54-sqm",
    "19000 $": "red-sea-150-sqm",
    "18000 $": "red-sea-135-sqm",
  };

  const profileData: Profile[] = [
    {
      key: "1",
      link: "/aa",
      image:
        "https://firebasestorage.googleapis.com/v0/b/olsdata-550ld.appspot.com/o/images%2F972.3499.olslm.png?alt=media&token=2fcf9749-a69e-483a-afca-465f5737911c",
      name: "Asvitloaten",
      olsDays: "2945",
      stats: "19931",
      olsm: "1952",
    },
    {
      key: "2",
      link: "/rwh",
      image:
        "https://images.crunchbase.com/image/upload/c_thumb,h_170,w_170,f_auto,g_face,z_0.7,b_white,q_auto:eco,dpr_2/mzatdfshnkstd0hnebmj",
      name: "ReiWenHo",
      olsDays: "2808",
      stats: "7170",
      olsm: "352",
    },
    {
      key: "3",
      link: "/ah",
      image:
        "https://media.licdn.com/dms/image/v2/D5603AQFO2oVdSlBbHw/profile-displayphoto-shrink_100_100/profile-displayphoto-shrink_100_100/0/1692414820173?e=1754524800&v=beta&t=RYQWjxzJr3F-Z9Nvl3Lx9aC-83PXG7QcrwuPBCK8B40",
      name: "A.H.",
      olsDays: "531",
      stats: "348",
      olsm: "3",
    },
  ];

  const columns = [
    {
      title: "OLSdays",
      dataIndex: "olsDays",
      key: "olsDays",
      width: 60,
      render: (text: string) => (
        <span style={{ color: "#ffffff", fontSize: 14 }}>{text}</span>
      ),
    },
    {
      title: "Profile",
      dataIndex: "profile",
      key: "profile",
      width: 140,
      render: (_: unknown, record: Profile) => (
        <a
          href={record.link}
          style={{
            textDecoration: "none",
            color: "#ffffff",
            display: "flex",
            alignItems: "center",
            gap: 10,
          }}
          aria-label={`View profile for ${record.name}`}
        >
          <img
            src={record.image}
            alt={`${record.name} profile`}
            style={{
              width: 32,
              height: 32,
              borderRadius: "50%",
              border: "1px solid rgba(255, 255, 255, 0.2)",
            }}
            onError={(e) => {
              (e.target as HTMLImageElement).src = "https://via.placeholder.com/40";
            }}
          />
          <span style={{ fontSize: 14, fontWeight: 500 }}>{record.name}</span>
        </a>
      ),
    },
    {
      title: () => (
        <span
          style={{
            background: "#cc9900",
            color: "#ffffff",
            padding: "4px 8px",
            borderRadius: "6px",
            fontSize: "14px",
            fontWeight: 600,
          }}
        >
          OLSLM
        </span>
      ),
      dataIndex: "stats",
      key: "stats",
      width: 120,
      render: (text: string) => (
        <span style={{ color: "#ffffff", fontSize: 14 }}>{text}</span>
      ),
    },
    {
      title: () => (
        <Link to="/olsme" style={{ textDecoration: "none" }}>
          <span
            style={{
              background: "#683fa0",
              color: "#ffffff",
              padding: "4px 8px",
              borderRadius: "6px",
              fontSize: "14px",
              fontWeight: 600,
            }}
          >
            OLSM
          </span>
        </Link>
      ),
      dataIndex: "olsm",
      key: "olsm",
      width: 100,
      render: (text: string) => (
        <span style={{ color: "#ffffff", fontSize: 14 }}>{text || "N/A"}</span>
      ),
    },
  ];

  const tableData = profileData.map((item) => ({
    key: item.key,
    link: item.link,
    image: item.image,
    name: item.name,
    olsDays: item.olsDays,
    stats: item.stats,
    olsm: item.olsm,
  }));

  return (
    <MonitorContainer className="login-monitor">
      <Helmet>
        <title>OLS Activity Monitor | OneLightSystem - Meditation & Assets</title>
        <meta
          name="description"
          content="Monitor OLS activity, student counts, asset metrics, and estate values for OneLightSystem's Sun Light Meditation Academy and Light Civilization Development."
        />
        <meta
          name="keywords"
          content="OLS activity monitor, Sun Light Meditation, OLS Meditation Education, Light Civilization, OLSLM assets, OLShealth"
        />
        <meta name="author" content="OneLightSystem OLS" />
        <meta name="viewport" content="width=device-width, initial-scale=1.0" />
        <meta property="og:title" content="OLS Activity Monitor - OneLightSystem" />
        <meta
          property="og:description"
          content="Track OLS student activity, assets, and estate values in real-time."
        />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://www.olsme.com/login-monitor" />
        <meta
          property="og:image"
          content="https://www.olsme.com/assets/ols-logo.png"
        />
        <meta property="og:image:alt" content="OLS Activity Monitor Logo" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="OLS Activity Monitor - OneLightSystem" />
        <meta
          name="twitter:description"
          content="Monitor OLS activity, student counts, and assets."
        />
        <meta
          property="twitter:image"
          content="https://www.olsme.com/assets/ols-logo.png"
        />
        <link rel="canonical" href="https://www.olsme.com/login-monitor" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebPage",
            name: "OLS Activity Monitor",
            description:
              "Monitor OLS activity, student counts, asset metrics, and estate values.",
            url: "https://www.olsme.com/login-monitor",
            breadcrumb: {
              "@type": "BreadcrumbList",
              itemListElement: [
                {
                  "@type": "ListItem",
                  position: 1,
                  name: "Home",
                  item: "https://www.olsme.com/",
                },
                {
                  "@type": "ListItem",
                  position: 2,
                  name: "Login Monitor",
                  item: "https://www.olsme.com/login-monitor",
                },
              ],
            },
            publisher: {
              "@type": "Organization",
              name: "OneLightSystem OLS",
              url: "https://www.olsme.com",
            },
          })}
        </script>
      </Helmet>

      {/* Section 1: Student Count and Table */}
      <StudentCountSection>
        <LogoImage src="logo.jpg" alt="OLS Logo" aria-label="OLS Logo" />
        <StudentCountLink
          to="https://www.olsme.com/Stat"
          aria-label="View student statistics"
        >
          <Text strong style={{ fontSize: 22, color: "#ffffff" }}>
            {salesData.students}
          </Text>
        </StudentCountLink>
        <ActionButton href="/register" $bgColor="#3552fc" $color="#ffffff">
          Apply
        </ActionButton>
      </StudentCountSection>

      <ProfileContainer>
        <Table
          className="styled-table"
          columns={columns}
          dataSource={tableData}
          pagination={false}
          size="middle"
          scroll={{ x: 600 }}
        />
      </ProfileContainer>

      {/* Section 2: Light Savings and aOLSLM Assets (Horizontal) */}
      <AssetsWrapper>
        <LightSavingsSection>
          <Text strong style={{ fontSize: 20, color: "#ffa500", display: "block", marginBottom: "10px" }}>
            Light Savings Estimation
          </Text>
          <MiniEstOLSLM />
        </LightSavingsSection>

        <AssetSection>
          <Text strong style={{ fontSize: 20, color: "#ffa500", display: "block", marginBottom: "10px" }}>
            aOLSLM Assets
          </Text>
          <div className="assets-buttons">
            {salesData.assets.slice(0, 2).map((asset, index) => (
              <ActionButton
                key={index}
                href={asset.link}
                $bgColor="#ff8c00"
                $color="#000000"
              >
                {asset.value}
              </ActionButton>
            ))}
          </div>
          {salesData.assets.slice(2).map((asset, index) => (
            <div key={index} className="asset-item">
              <SalesHighlight style={{ marginRight: 10 }}>
                {asset.value}
              </SalesHighlight>
              <ActionButton
                href={asset.link}
                $bgColor="#3552fc"
                $color="#ffffff"
              >
                {asset.label.split(" - ")[1] || "Reserve"}
              </ActionButton>
              <Text style={{ paddingLeft: 10, color: "#ff4d4f" }}>
                {asset.percentage}
              </Text>
            </div>
          ))}
        </AssetSection>
      </AssetsWrapper>

      {/* Section 3: OLSME */}
      <OlsmeSection>
        <Text strong style={{ fontSize: 20, color: "#683fa0", display: "block", marginBottom: "10px" }}>
        </Text>
        <MiniOlsmeMonitor />
      </OlsmeSection>

      {/* Bottom Sections: Estate and OLShealth */}
      <div className="bottom-sections" style={{ display: "flex", gap: 24, marginTop: 32, width: "100%" }}>
        <div
          className="estate-section"
          style={{
        cursor: "pointer",
        display: "flex",
        alignItems: "center",
        background: "rgba(0, 204, 0, 0.08)",
        borderRadius: 10,
        padding: "18px 28px",
        boxShadow: "0 2px 12px rgba(0,204,0,0.08)",
        border: "1.5px solid #00cc00",
        minWidth: 220,
        flex: 1,
        transition: "background 0.2s, box-shadow 0.2s",
          }}
          onClick={toggleEstate}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => e.key === "Enter" && toggleEstate()}
          aria-label="Toggle estate values"
        >
          <RightOutlined
        style={{ paddingRight: 14, color: "#00cc00", fontSize: 22 }}
          />
          <Text strong style={{ color: "#00cc00", fontSize: 20, letterSpacing: 1 }}>
        ESTATE
          </Text>
          <div className="estate-buttons" style={{ marginLeft: "auto" }}>
        <ActionButton
          href="/estate"
          $bgColor="#00cc00"
          $color="#ffffff"
          $large={true}
          style={{
            fontWeight: 600,
            fontSize: 17,
            marginLeft: 12,
            boxShadow: "0 2px 8px rgba(0,204,0,0.12)",
            border: "1.5px solid #00cc00",
            letterSpacing: "0.5px",
          }}
        >
          Ownership
        </ActionButton>
          </div>
        </div>

        <div className="olshealth-section">
            <ActionButton
              href="/OLShealth"
              $bgColor="#ffec3d"
              $color="#222222"
              $large={true}
              style={{
              boxShadow: "0 2px 8px rgba(255, 236, 61, 0.25)",
              border: "1.5px solid #ffe066",
              fontWeight: 600,
              letterSpacing: "0.5px",
              }}
            >
              <span style={{ color: "#b8860b" }}>OLS</span>
              <span style={{ color: "#222222", marginLeft: 2 }}>health</span>
            </ActionButton>
        </div>
      </div>

      {state?.isEstateOpen && (
        <div className="estate-values">
          {salesData.estateValues.map((value, index) => (
            <div key={index} className="estate-value-item">
              <EstateValueLink to={`/estate/${estateValueToIdMap[value]}`}>
                <SalesHighlight style={{ marginRight: 20 }}>{value}</SalesHighlight>
              </EstateValueLink>
            </div>
          ))}
        </div>
      )}
    </MonitorContainer>
  );
};

export default React.memo(LoginMonitor);